/**
 * Business Categories Management Page
 *
 * Admin page to manage business/industry categories
 * These categories determine which industry-specific documents are required
 */

import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Icon,
  Chip,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import WDButton from "../../../../MaterialComponents/WDButton";
import {
  updateScreenTitle,
  showSuccessMessage,
  showLoader,
  dismissLoader,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../../Configurations/Appservice";
import BusinessCategoryFormDialog from "./BusinessCategoryFormDialog";

const BusinessCategoriesPage = () => {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    dispatch(updateScreenTitle("Business Categories"));
    loadCategories();
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const loadCategories = () => {
    dispatch(showLoader());
    AppService.get("api/business-categories/list-details")
      .then((response) => {
        setCategories(response || []);
      })
      .catch((error) => {
        console.error("Failed to load business categories", error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleSaveCategory = (categoryData) => {
    dispatch(showLoader());
    if (selectedCategory) {
      // Update existing
      const updateData = {
        id: selectedCategory.id,
        ...categoryData,
      };
      AppService.put(`api/business-categories/${selectedCategory.id}`, updateData)
        .then(() => {
          dispatch(showSuccessMessage("Business category updated successfully"));
          loadCategories();
          handleCloseDialog();
        })
        .catch((error) => {
          console.error("Failed to update business category", error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    } else {
      // Create new
      AppService.post("api/business-categories", categoryData)
        .then(() => {
          dispatch(showSuccessMessage("Business category created successfully"));
          loadCategories();
          handleCloseDialog();
        })
        .catch((error) => {
          console.error("Failed to create business category", error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  const handleToggleStatus = (category) => {
    dispatch(showLoader());
    const updateData = {
      id: category.id,
      code: category.code,
      name: category.name,
      nameAr: category.nameAr,
      description: category.description,
      colour: category.colour,
      icon: category.icon,
      licenseActivityCodes: category.licenseActivityCodes || [],
      documents: (category.documents || []).map(d => ({
        documentTypeId: d.documentTypeId,
        isMandatory: d.isMandatory,
      })),
      isActive: !category.isActive,
    };
    AppService.put(`api/business-categories/${category.id}`, updateData)
      .then(() => {
        dispatch(
          showSuccessMessage(
            `Category ${category.isActive ? "deactivated" : "activated"} successfully`
          )
        );
        loadCategories();
      })
      .catch((error) => {
        console.error("Failed to toggle category status", error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const filteredCategories = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <WDBox>
      <Card sx={{ p: 3 }}>
        {/* Header */}
        <WDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <WDBox>
            <WDTypography variant="h6" fontWeight="medium">
              Business Categories
            </WDTypography>
            <WDTypography variant="body2" color="text.secondary">
              Manage industry classifications for organizations
            </WDTypography>
          </WDBox>
          <WDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>add</Icon>}
            onClick={handleAddCategory}
          >
            Add Category
          </WDButton>
        </WDBox>

        {/* Search */}
        <WDBox mb={3}>
          <TextField
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon>search</Icon>
                </InputAdornment>
              ),
            }}
          />
        </WDBox>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Icon</TableCell>
                <TableCell>Category Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell align="center">Documents</TableCell>
                <TableCell align="center">Organizations</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCategories.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: `${category.colour}20`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: category.colour }}>{category.icon}</Icon>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <WDTypography variant="body2" fontWeight="medium">
                      {category.name}
                    </WDTypography>
                    {category.nameAr && (
                      <WDTypography variant="caption" color="text.secondary">
                        {category.nameAr}
                      </WDTypography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={category.code} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.documentCount || 0}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.organizationCount || 0}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.isActive ? "Active" : "Inactive"}
                      color={category.isActive ? "success" : "default"}
                      size="small"
                      onClick={() => handleToggleStatus(category)}
                      sx={{ cursor: "pointer" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditCategory(category)}
                      title="Edit"
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredCategories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <WDTypography variant="body2" color="text.secondary" py={3}>
                      No business categories found
                    </WDTypography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Dialog */}
      <BusinessCategoryFormDialog
        open={dialogOpen}
        category={selectedCategory}
        onClose={handleCloseDialog}
        onSave={handleSaveCategory}
      />
    </WDBox>
  );
};

export default BusinessCategoriesPage;
